/**
 * InfoModal — reusable informational guide modal.
 *
 * Accepts a title, subtitle, and an array of section cards. Used by the
 * various contextual "?" / ⓘ info buttons throughout the app.
 */

import { X, Info } from 'lucide-react';
import type { ReactNode } from 'react';

export interface InfoSection {
  id: string;
  label: string;
  /** Optional sub-label / axis */
  meta?: string;
  tagline?: string;
  description: string;
  examples?: string[];
  note?: string;
  color?: string;       // Tailwind bg + border classes, e.g. "bg-cyan-50 border-cyan-200"
  iconColor?: string;   // Tailwind text class, e.g. "text-cyan-600"
  Icon?: React.ElementType;
}

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  sections: InfoSection[];
  /** Optional footer note */
  footerNote?: ReactNode;
}

export function InfoModal({ isOpen, onClose, title, subtitle, sections, footerNote }: InfoModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>
              {subtitle && <p className="text-sm text-[var(--text-ter)]">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)] flex-shrink-0">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {sections.map((s) => (
            <div
              key={s.id}
              className={` border p-4 ${s.color ?? 'bg-[var(--surface)] border-[var(--rule)]'}`}
            >
              <div className="flex items-start gap-3">
                {s.Icon && (
                  <div className={`flex-shrink-0 mt-0.5 ${s.iconColor ?? 'text-[var(--text-ter)]'}`}>
                    <s.Icon className="h-5 w-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-[var(--text-pri)]">{s.label}</span>
                    {s.meta && <span className="text-xs text-[var(--text-ter)]">{s.meta}</span>}
                  </div>
                  {s.tagline && (
                    <p className="text-sm font-medium text-[var(--text-sec)] mb-1 italic">"{s.tagline}"</p>
                  )}
                  <p className="text-sm text-[var(--text-sec)] mb-2">{s.description}</p>
                  {s.examples && s.examples.length > 0 && (
                    <div className="space-y-0.5 mb-2">
                      <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Examples</p>
                      <ul className="space-y-0.5">
                        {s.examples.map((ex, i) => (
                          <li key={i} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                            <span className="flex-shrink-0 mt-0.5 opacity-40">·</span>
                            {ex}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {s.note && (
                    <p className="text-xs text-[var(--text-ter)] italic">{s.note}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {footerNote && (
            <div className="mt-2 px-1 text-xs text-[var(--text-ter)]">
              {footerNote}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t bg-[var(--surface)] flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Small circular info button that opens an InfoModal */
export function InfoButton({ onClick, title = 'Learn more', className = '' }: { onClick: () => void; title?: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex-shrink-0 p-1 rounded-full text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors ${className}`}
    >
      <Info className="h-4 w-4" />
    </button>
  );
}
